import { formatINR } from '../../../shared/utils/formatters';
import React, { useState } from 'react'
import { Card, Table, Tag, Space, Button, Typography, Modal, Form, Input, message, Statistic, Row, Col } from 'antd'
import { CheckCircleOutlined, DollarOutlined, FilePdfOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../../shared/api/axios-instance'
import { config } from '../../../shared/config/env'

const { Title, Text } = Typography

export const MarketplaceInvoicesScreen: React.FC = () => {
    const queryClient = useQueryClient()
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [isDepositModalOpen, setIsDepositModalOpen] = useState(false)
    const [form] = Form.useForm()

    const { data, isLoading } = useQuery({
        queryKey: ['marketplace-invoices'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/marketplace-invoices')
            return response.data.data
        }
    })

    const depositMutation = useMutation({
        mutationFn: async (values: { challan_number: string }) => {
            const response = await apiClient.post('/admin/marketplace-invoices/tds-deposit', {
                invoice_ids: selectedRowKeys,
                challan_number: values.challan_number
            })
            return response.data
        },
        onSuccess: () => {
            message.success('TDS deposit recorded successfully')
            setIsDepositModalOpen(false)
            setSelectedRowKeys([])
            form.resetFields()
            queryClient.invalidateQueries({ queryKey: ['marketplace-invoices'] })
        },
        onError: () => {
            message.error('Failed to record TDS deposit')
        }
    })
    
    const handleViewInvoice = async (id: number) => {
        try {
            const response = await apiClient.get(`/admin/marketplace-invoices/${id}/download`, {
                responseType: 'blob'
            })
            
            if (response.data.type === 'application/json') {
                // The server returned a JSON error hidden in a blob
                const text = await response.data.text()
                const error = JSON.parse(text)
                message.error(error.message || 'Failed to load invoice')
                return
            }

            const blob = new Blob([response.data], { type: 'application/pdf' })
            const url = window.URL.createObjectURL(blob)
            window.open(url, '_blank')
            
            // Cleanup memory after a delay
            setTimeout(() => window.URL.revokeObjectURL(url), 60000)
        } catch (error) {
            message.error('Failed to connect to invoice service')
        }
    }

    const columns = [
        { title: 'Invoice #', dataIndex: 'invoice_number', key: 'invoice_number' },
        { title: 'Order ID', dataIndex: 'order_id', key: 'order_id' },
        { title: 'Merchant ID', dataIndex: 'merchant_id', key: 'merchant_id' },
        { 
            title: 'Order Amount', 
            dataIndex: 'order_amount', 
            key: 'order_amount',
            render: (val: any) => formatINR(Number(val || 0).toFixed(2))
        },
        { 
            title: 'Commission', 
            dataIndex: 'commission_amount', 
            key: 'commission_amount',
            render: (val: any) => formatINR(Number(val || 0).toFixed(2))
        },
        { 
            title: 'GST (18%)', 
            dataIndex: 'gst_amount', 
            key: 'gst_amount',
            render: (val: any) => formatINR(Number(val || 0).toFixed(2))
        },
        { 
            title: 'TDS (0.1%)', 
            dataIndex: 'tds_amount', 
            key: 'tds_amount',
            render: (val: any) => formatINR(Number(val || 0).toFixed(2))
        },
        { 
            title: 'Total', 
            dataIndex: 'total_amount', 
            key: 'total_amount',
            render: (val: any) => formatINR(Number(val || 0).toFixed(2))
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'PAID' ? 'green' : 'gold'}>{status}</Tag>
            )
        },
        {
            title: 'TDS Status',
            dataIndex: 'tds_status',
            key: 'tds_status',
            render: (status: string) => (
                <Tag color={status === 'DEPOSITED' ? 'blue' : 'orange'}>{status}</Tag>
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: any) => (
                <Button 
                    type="link" 
                    icon={<FilePdfOutlined />} 
                    onClick={() => handleViewInvoice(record.id)}
                >
                    View
                </Button>
            )
        }
    ]

    const totalTdsPending = data?.filter((i: any) => i.tds_status === 'PENDING')
        .reduce((sum: number, i: any) => sum + parseFloat(i.tds_amount), 0) || 0

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Title level={2}>Marketplace Invoices</Title>
            
            <Row gutter={16}>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="Pending TDS Deposit"
                            value={totalTdsPending}
                            precision={2}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong>{selectedRowKeys.length} items selected</Text>
                    <Button 
                        type="primary" 
                        disabled={selectedRowKeys.length === 0}
                        icon={<CheckCircleOutlined />}
                        onClick={() => setIsDepositModalOpen(true)}
                    >
                        Mark TDS as Deposited
                    </Button>
                </div>
                
                <Table 
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys),
                        getCheckboxProps: (record: any) => ({
                            disabled: record.tds_status === 'DEPOSITED',
                        }),
                    }}
                    columns={columns} 
                    dataSource={data} 
                    loading={isLoading}
                    rowKey="id"
                />
            </Card>

            <Modal
                title="Record TDS Deposit"
                open={isDepositModalOpen}
                onOk={() => form.submit()}
                onCancel={() => setIsDepositModalOpen(false)}
                confirmLoading={depositMutation.isPending}
            >
                <Form form={form} layout="vertical" onFinish={(v) => depositMutation.mutate(v)}>
                    <Form.Item 
                        name="challan_number" 
                        label="Challan Number / Transaction Reference"
                        rules={[{ required: true, message: 'Please enter challan number' }]}
                    >
                        <Input placeholder="Enter government challan number" />
                    </Form.Item>
                    <Text type="secondary">
                        This will mark {selectedRowKeys.length} invoices as TDS Deposited.
                    </Text>
                </Form>
            </Modal>
        </Space>
    )
}
